var __jsx = React.createElement;
import React, { useState, useRef, useEffect } from "react";
import AwesomeIcon from "../../AwesomeIcon";
import { faPaperPlaneTop, faMicrophone, faUserTag, faWaveformLines, faTimes, faStop, faSearch, faMemoPad, faStar, faFileLines, faArrowsRotate, faLayerGroup } from "@fortawesome/pro-regular-svg-icons";
import classNames from "classnames";
import { useStoreState } from "pullstate";
import Store from "../../../utils/Store";
import constants from "../../../utils/constants";
import { isEmpty, truncate, get } from "lodash";
import ComingSoonPopup from "../ComingSoonPopup";
import promptsData from "../PromptsModal/prompts.json";
import { trackEvent } from "../../../utils/mixpanel";
import { MIXPANEL_EVENTS } from "../../../utils/mixpanelEvents";
var placeholders = ["Type `/` to see all available actions and prompts.", "Click the mic and Speak: control the assistant handsfree.", "Try `/add patient` to quickly add a new patient.", "Type `/list forms` to see and fill available forms."];
var flattenPrompts = function flattenPrompts(data) {
  return data.sections.flatMap(function (section) {
    return section.prompts.map(function (prompt) {
      return {
        name: prompt.name,
        content: prompt.content,
        section: section.title,
        icon: section.icon
      };
    });
  });
};
var allPrompts = flattenPrompts(promptsData);

// Icon mapping for prompt section headers
var sectionIconMap = {
  faUserTag: faUserTag,
  faSearch: faSearch,
  faMemoPad: faMemoPad,
  faStar: faStar,
  faFileLines: faFileLines,
  faArrowsRotate: faArrowsRotate,
  faLayerGroup: faLayerGroup
};
var ChatInput = function ChatInput(_ref) {
  var onSend = _ref.onSend,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$inProgress = _ref.inProgress,
    inProgress = _ref$inProgress === void 0 ? false : _ref$inProgress;
  var _useState = useState(""),
    inputValue = _useState[0],
    setInputValue = _useState[1];
  var textareaRef = useRef(null);
  var _useState2 = useState(false),
    isFocused = _useState2[0],
    setIsFocused = _useState2[1];
  var _useState3 = useState(0),
    currentPlaceholder = _useState3[0],
    setCurrentPlaceholder = _useState3[1];
  var _useState4 = useState(true),
    isPlaceholderVisible = _useState4[0],
    setIsPlaceholderVisible = _useState4[1];
  var _useState5 = useState(null),
    showComingSoonPopup = _useState5[0],
    setShowComingSoonPopup = _useState5[1];
  var _useState6 = useState(false),
    isTranscribing = _useState6[0],
    setIsTranscribing = _useState6[1];
  var recognitionRef = useRef(null);
  var transcribeTimeoutRef = useRef(null);
  var _useState7 = useState(""),
    interimTranscript = _useState7[0],
    setInterimTranscript = _useState7[1];
  var _useState8 = useState(false),
    showPromptDropdown = _useState8[0],
    setShowPromptDropdown = _useState8[1];
  var _useState9 = useState(""),
    promptQuery = _useState9[0],
    setPromptQuery = _useState9[1];
  var _useState0 = useState(allPrompts),
    filteredPrompts = _useState0[0],
    setFilteredPrompts = _useState0[1];
  var _useState1 = useState(0),
    selectedPromptIndex = _useState1[0],
    setSelectedPromptIndex = _useState1[1];
  var textareaContainerRef = useRef(null);
  var promptItemRefs = useRef([]);
  var _useState10 = useState(null),
    selection = _useState10[0],
    setSelection = _useState10[1];
  var baseInputValueRef = useRef("");
  var baseSelectionRef = useRef(null);
  // Track prompt modal open (slash) only on first open per session
  var promptDropdownOpenedRef = useRef(false);

  // Get selected patient from Store
  var _useStoreState = useStoreState(Store, function (s) {
      return {
        patientDetail: s.patientDetail
      };
    }),
    patientDetail = _useStoreState.patientDetail;
  var _useStoreState2 = useStoreState(Store, function (s) {
      return {
        chatInputContent: s.chatInputContent,
        isDraggableModalOpen: s.isDraggableModalOpen
      };
    }),
    chatInputContent = _useStoreState2.chatInputContent,
    isDraggableModalOpen = _useStoreState2.isDraggableModalOpen;

  // Rotate placeholders every 8 seconds with fade animation
  useEffect(function () {
    var interval;
    var timeout;
    var startRotation = function startRotation() {
      interval = setInterval(function () {
        setIsPlaceholderVisible(false);
        timeout = setTimeout(function () {
          setCurrentPlaceholder(function (prev) {
            return (prev + 1) % placeholders.length;
          });
          setIsPlaceholderVisible(true);
        }, 500);
      }, 6000);
    };
    var stopRotation = function stopRotation() {
      if (interval) {
        clearInterval(interval);
      }
      if (timeout) {
        clearTimeout(timeout);
      }
      setIsPlaceholderVisible(true);
    };
    if (!inputValue.trim()) {
      startRotation();
    } else {
      stopRotation();
    }
    return function () {
      stopRotation();
    };
  }, [inputValue]); // Add inputValue as dependency to react to changes

  // Add clipboard monitoring
  useEffect(function () {
    if (isDraggableModalOpen && chatInputContent) {
      setInputValue(chatInputContent);
      setTimeout(function () {
        Store.update(function (s) {
          s.chatInputContent = "";
        });
      }, 900);
    }
  }, [isDraggableModalOpen]);
  useEffect(function () {
    if (!inProgress) {
      var _textareaRef$current;
      (_textareaRef$current = textareaRef.current) === null || _textareaRef$current === void 0 || _textareaRef$current.focus();
    }
  }, [inProgress]);

  // Auto-resize textarea
  useEffect(function () {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = "".concat(Math.min(textareaRef.current.scrollHeight, 200), "px");
    }
  }, [inputValue, isDraggableModalOpen]);

  // Reset textarea height when modal is minimized
  useEffect(function () {
    if (!isDraggableModalOpen && textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = "60px"; // Set a default height when minimized
    }
  }, [isDraggableModalOpen]);

  // Clean up recognition and timer on unmount
  useEffect(function () {
    return function () {
      if (recognitionRef.current) {
        recognitionRef.current.onresult = null;
        recognitionRef.current.onerror = null;
        recognitionRef.current.onend = null;
        recognitionRef.current.stop();
      }
      if (transcribeTimeoutRef.current) {
        clearTimeout(transcribeTimeoutRef.current);
      }
    };
  }, []);
  useEffect(function () {
    var handleShortcut = function handleShortcut(e) {
      // CMD+K (Mac) or CTRL+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        // Only trigger if input is visible (optionally: isDraggableModalOpen)
        if (!disabled && !inProgress) {
          handleMicClick();
        }
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return function () {
      return window.removeEventListener("keydown", handleShortcut);
    };
  }, [disabled, inProgress, isTranscribing]);

  // Detect slash command and update dropdown
  useEffect(function () {
    var match = inputValue.match(/(^|\s)\/(\w*)$/);
    if (match) {
      setShowPromptDropdown(true);
      setPromptQuery(match[2] || "");
      var filtered = allPrompts.filter(function (p) {
        return p.name.toLowerCase().includes((match[2] || "").toLowerCase());
      });
      setFilteredPrompts(filtered);
      setSelectedPromptIndex(0);
    } else {
      setShowPromptDropdown(false);
      setPromptQuery("");
      setFilteredPrompts(allPrompts);
      setSelectedPromptIndex(0);
    }
  }, [inputValue]);

  // Scroll selected prompt into view when selectedPromptIndex changes
  useEffect(function () {
    if (showPromptDropdown && filteredPrompts.length > 0) {
      var ref = promptItemRefs.current[selectedPromptIndex];
      if (ref) {
        ref.scrollIntoView({
          block: "nearest"
        });
      }
    }
  }, [selectedPromptIndex, showPromptDropdown, filteredPrompts]);

  // Track prompt modal open (slash) only on first open per session
  useEffect(function () {
    if (showPromptDropdown && !promptDropdownOpenedRef.current) {
      trackEvent(MIXPANEL_EVENTS.ASSISTANT_PROMPT_MODAL_OPEN, {
        method: "slash"
      });
      promptDropdownOpenedRef.current = true;
    }
    if (!showPromptDropdown) {
      promptDropdownOpenedRef.current = false;
    }
  }, [showPromptDropdown]);
  var handleSubmit = function handleSubmit(e) {
    e === null || e === void 0 || e.preventDefault();
    // Stop recording if active
    if (isTranscribing) {
      stopRecognition();
    }
    if (inputValue.trim() && !disabled && !inProgress) {
      // Track send via button
      if (e && e.type === "click") {
        trackEvent(MIXPANEL_EVENTS.ASSISTANT_CHATINPUT_SEND_BUTTON);
      }
      onSend(inputValue);
      setInputValue("");
    }
    Store.update(function (s) {
      s.lastAssitantMessageId = "";
    });
  };
  var handleKeyDown = function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      // Track send via enter
      trackEvent(MIXPANEL_EVENTS.ASSISTANT_CHATINPUT_SEND_ENTER);
      handleSubmit();
    }
  };
  var getGender = function getGender(gender) {
    var genderObj = constants.GENDERS.find(function (g) {
      return g.value === gender;
    });
    return genderObj === null || genderObj === void 0 ? void 0 : genderObj.shortLabel;
  };
  var onClickOpenPromptsModal = function onClickOpenPromptsModal() {
    var dummyAnchor = document.createElement("a");
    dummyAnchor.href = "#prompts";
    dummyAnchor.click();
    Store.update(function (s) {
      s.isDraggableModalOpen = !s.isDraggableModalOpen;
    });
  };
  var getPatientStatus = function getPatientStatus() {
    var aiInference = get(patientDetail, ["latest_study", "ai_inference"]);
    if (aiInference === constants.AI_INFERENCE.POSITIVE) {
      return {
        isAbnormal: true,
        label: "Abnormal"
      };
    } else if (aiInference === constants.AI_INFERENCE.NEGATIVE) {
      return {
        isAbnormal: false,
        label: "Normal"
      };
    }
    return null;
  };
  var patientStatus = getPatientStatus();

  // Handlers for accept/cancel
  var stopRecognition = function stopRecognition() {
    var _recognitionRef$curre;
    (_recognitionRef$curre = recognitionRef.current) === null || _recognitionRef$curre === void 0 || _recognitionRef$curre.stop();
    setIsTranscribing(false);
    if (transcribeTimeoutRef.current) {
      clearTimeout(transcribeTimeoutRef.current);
    }
    // Track stop recording
    trackEvent(MIXPANEL_EVENTS.ASSISTANT_CHATINPUT_STOP_RECORDING);
  };

  // Update selection on textarea events
  var handleSelect = function handleSelect(e) {
    var sel = {
      start: e.currentTarget.selectionStart,
      end: e.currentTarget.selectionEnd
    };
    setSelection(sel);
    baseSelectionRef.current = sel;
  };

  // Insert or replace text at selection
  var insertAtSelection = function insertAtSelection(text, baseValue, selection) {
    if (!selection) return baseValue + text;
    var start = selection.start,
      end = selection.end;
    return baseValue.slice(0, start) + text + baseValue.slice(end);
  };

  // Handle speech recognition results
  var handleSpeechResult = function handleSpeechResult(_final, interim) {
    // Always apply to baseInputValueRef/baseSelectionRef
    if (_final) {
      var baseValue = baseInputValueRef.current;
      var sel = baseSelectionRef.current || {
        start: baseValue.length,
        end: baseValue.length
      };
      var newValue = insertAtSelection(_final, baseValue, sel);
      setInputValue(newValue);
      baseInputValueRef.current = newValue;
      // Move caret after inserted text
      setTimeout(function () {
        if (textareaRef.current) {
          var newPos = sel.start + _final.length;
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(newPos, newPos);
          setSelection({
            start: newPos,
            end: newPos
          });
          baseSelectionRef.current = {
            start: newPos,
            end: newPos
          };
        }
      }, 0);
      setInterimTranscript("");
    } else {
      setInterimTranscript(interim);
    }
  };
  var handleMicClick = function handleMicClick() {
    if (isTranscribing) {
      stopRecognition();
      return;
    }
    // Track speak button
    trackEvent(MIXPANEL_EVENTS.ASSISTANT_CHATINPUT_SPEAK);
    // Set baseInputValue and baseSelection to current state when starting transcription
    if (textareaRef.current) {
      baseInputValueRef.current = inputValue;
      baseSelectionRef.current = {
        start: textareaRef.current.selectionStart,
        end: textareaRef.current.selectionEnd
      };
    } else {
      baseInputValueRef.current = inputValue;
      baseSelectionRef.current = selection || {
        start: inputValue.length,
        end: inputValue.length
      };
    }
    var SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      alert("Speech recognition is not supported in this browser.");
      return;
    }
    var recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.interimResults = true;
    recognition.continuous = true;
    recognition.onresult = function (event) {
      var interim = "";
      var _final2 = "";
      for (var i = event.resultIndex; i < event.results.length; ++i) {
        var transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          _final2 += transcript;
        } else {
          interim += transcript;
        }
      }
      handleSpeechResult(_final2, interim);
    };
    recognition.onerror = function (event) {
      setIsTranscribing(false);
      if (transcribeTimeoutRef.current) {
        clearTimeout(transcribeTimeoutRef.current);
      }
    };
    recognition.onend = function () {
      setIsTranscribing(false);
      if (transcribeTimeoutRef.current) {
        clearTimeout(transcribeTimeoutRef.current);
      }
    };
    recognitionRef.current = recognition;
    setIsTranscribing(true);
    setInterimTranscript("");
    recognition.start();
  };

  // Keyboard navigation for prompt dropdown
  var handlePromptKeyDown = function handlePromptKeyDown(e) {
    if (!showPromptDropdown || filteredPrompts.length === 0) return;
    e.stopPropagation();
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedPromptIndex(function (i) {
        return (i + 1) % filteredPrompts.length;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedPromptIndex(function (i) {
        return (i - 1 + filteredPrompts.length) % filteredPrompts.length;
      });
    } else if (e.key === "Tab" || e.key === "Enter") {
      e.preventDefault();
      if (filteredPrompts[selectedPromptIndex]) {
        selectPrompt(filteredPrompts[selectedPromptIndex]);
      }
    } else if (e.key === "Escape") {
      setShowPromptDropdown(false);
    }
  };

  // Insert prompt content into input and place cursor at end
  var selectPrompt = function selectPrompt(prompt) {
    // Track prompt selected
    trackEvent(MIXPANEL_EVENTS.ASSISTANT_PROMPT_SELECTED, {
      promptName: prompt.name
    });
    setInputValue(function (prev) {
      var newValue = prev.replace(/(^|\s)\/(\w*)$/, "$1".concat(prompt.content));
      setTimeout(function () {
        if (textareaRef.current) {
          textareaRef.current.focus();
          textareaRef.current.setSelectionRange(newValue.length, newValue.length);
        }
      }, 0);
      return newValue;
    });
    setShowPromptDropdown(false);
    setPromptQuery("");
    setFilteredPrompts(allPrompts);
    setSelectedPromptIndex(0);
  };

  // Attach keydown handler to textarea
  useEffect(function () {
    var textarea = textareaRef.current;
    if (!textarea) return;
    var handler = function handler(e) {
      return handlePromptKeyDown(e);
    };
    textarea.addEventListener("keydown", handler);
    return function () {
      return textarea.removeEventListener("keydown", handler);
    };
  }, [showPromptDropdown, filteredPrompts, selectedPromptIndex]);
  return /*#__PURE__*/React.createElement("form", {
    onSubmit: handleSubmit,
    className: "relative w-full"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex flex-col w-full p-4 border-t border-gray-800 bg-transparent"
  }, /*#__PURE__*/React.createElement("div", {
    ref: textareaContainerRef,
    className: classNames("relative w-full transition-all duration-200 border rounded-2xl overflow-hidden",
    // 🛠️ Add `overflow-hidden`
    isTranscribing ? "border-[1.5px] border-[#FF7869]/60 animate-[chatinput-red-border-pulse_1s_infinite]" : isFocused ? "border-teal-500 shadow-[0_3px_10px_rgba(30,40,50,0.3)]" : "border-gray-700 shadow-[0_3px_8px_rgba(20,25,35,0.25)]")
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames("flex items-center px-4 py-3 dark:bg-[#232830] bg-gray-600 rounded-t-2xl border-b border-gray-700", {
      hidden: isEmpty(patientDetail === null || patientDetail === void 0 ? void 0 : patientDetail.patient_id)
    })
  }, /*#__PURE__*/React.createElement("div", {
    className: "relative"
  }, /*#__PURE__*/React.createElement("div", {
    className: "p-3 rounded-full bg-teal-100/20 dark:bg-teal-900 flex items-center justify-center"
  }, /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faUserTag,
    size: "lg",
    className: "text-teal-200"
  }))), /*#__PURE__*/React.createElement("div", {
    className: "font-bold break-words break-all text-white mx-2"
  }, truncate(patientDetail.name, {
    length: 20
  })), /*#__PURE__*/React.createElement("div", {
    className: "text-gray-300 text-md mr-3"
  }, patientDetail.age, "Y ", /*#__PURE__*/React.createElement("span", null, getGender(patientDetail.gender))), /*#__PURE__*/React.createElement("div", {
    className: "text-gray-300 mr-2"
  }, patientDetail.id), patientStatus && /*#__PURE__*/React.createElement("div", {
    className: "px-4 py-2 bg-gray-900 rounded-full font-medium text-gray-200 flex items-center"
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames("h-3 w-3 rounded-full mr-2", patientStatus.isAbnormal ? "bg-red-500" : "bg-green-500")
  }), /*#__PURE__*/React.createElement("span", {
    className: "text-md"
  }, patientStatus.label))), /*#__PURE__*/React.createElement("div", {
    className: "relative px-4 py-2 dark:bg-[#2b333d] bg-gray-200"
  }, /*#__PURE__*/React.createElement("div", {
    className: "relative"
  }, /*#__PURE__*/React.createElement("textarea", {
    ref: textareaRef,
    value: function () {
      if (isTranscribing && interimTranscript) {
        // Show interim transcript at selection, but do not update inputValue
        var baseValue = baseInputValueRef.current;
        var sel = baseSelectionRef.current || {
          start: baseValue.length,
          end: baseValue.length
        };
        return insertAtSelection(interimTranscript, baseValue, sel);
      }
      return inputValue;
    }(),
    onChange: function onChange(e) {
      setInputValue(e.target.value);
      baseInputValueRef.current = e.target.value;
      var sel = {
        start: e.target.selectionStart,
        end: e.target.selectionEnd
      };
      setSelection(sel);
      baseSelectionRef.current = sel;
    },
    onKeyDown: handleKeyDown,
    onFocus: function onFocus() {
      return setIsFocused(true);
    },
    onBlur: function onBlur() {
      return setIsFocused(false);
    },
    onSelect: handleSelect,
    placeholder: placeholders[currentPlaceholder],
    disabled: inProgress,
    className: classNames("w-full px-4 py-3 pr-6 bg-transparent text-gray-700  dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 resize-none disabled:opacity-50 disabled:cursor-not-allowed border-0 focus:border-0 focus:outline-none", "transition-opacity duration-500 min-h-[60px]", "scrollbar-light dark:scrollbar"),
    style: {
      maxHeight: isDraggableModalOpen ? "200px" : "40px",
      overflowY: "auto",
      opacity: isPlaceholderVisible ? 1 : 0
    },
    rows: 1
  })), /*#__PURE__*/React.createElement("div", {
    className: classNames("absolute right-3 top-1/2 -translate-y-1/2 transition-all duration-300 ease-in-out", inputValue.trim() ? "opacity-100 transform translate-x-0" : "opacity-0 transform translate-x-4")
  }, /*#__PURE__*/React.createElement("button", {
    onClick: handleSubmit,
    disabled: inProgress,
    className: "p-3 cursor-pointer bg-teal-400 dark:bg-teal-800 dark:hover:bg-teal-900 hover:bg-teal-500 text-white rounded-full transition-all duration-200 transform hover:scale-105"
  }, /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faPaperPlaneTop,
    size: "lg",
    className: "text-white"
  })))), /*#__PURE__*/React.createElement("div", {
    className: "flex items-center px-5 py-3 bg-gray-600 dark:bg-[#1f2630] cursor-pointer border-t border-gray-200 dark:border-gray-700 rounded-b-2xl"
  }, isTranscribing && /*#__PURE__*/React.createElement("button", {
    type: "button",
    "aria-label": "Clear text",
    className: "flex items-center px-4 py-2 cursor-pointer group rounded-full text-gray-400 hover:text-[#FF7869] hover:bg-[#FF7869]/10 transition-all duration-200",
    onClick: function onClick() {
      // Track clear button
      trackEvent(MIXPANEL_EVENTS.ASSISTANT_CHATINPUT_CLEAR);
      setInputValue("");
      setInterimTranscript("");
      baseInputValueRef.current = "";
      baseSelectionRef.current = {
        start: 0,
        end: 0
      };
      setSelection({
        start: 0,
        end: 0
      });
    }
  }, /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faTimes,
    size: "lg"
  }), /*#__PURE__*/React.createElement("span", {
    className: "ml-2 text-base font-medium group-hover:opacity-100 translate-x-0 group-hover:translate-x-1 opacity-0 transition-all duration-200"
  }, "Clear")), /*#__PURE__*/React.createElement("div", {
    className: "flex-1"
  }), /*#__PURE__*/React.createElement("div", {
    className: "flex items-center relative transition-all duration-300"
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames("group flex items-center relative transition-all duration-300", isTranscribing ? "ml-auto" : "")
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: isTranscribing ? stopRecognition : handleMicClick,
    className: classNames("group flex items-center p-2 px-3 transition-colors duration-200", isTranscribing ? "text-[#FF7869]" : "text-gray-200 hover:text-teal-200 dark:text-gray-400 dark:hover:text-teal-600"),
    "aria-label": isTranscribing ? "Stop recording" : "Start recording",
    disabled: inProgress
  }, /*#__PURE__*/React.createElement("span", {
    className: classNames("mr-2 text-base font-medium whitespace-nowrap bg-gray-200 dark:bg-gray-900 px-3 py-1 rounded shadow-md border z-10 transition-all duration-300", "dark:text-[#FF7869] text-brand-peach border-[#FF7869]/30 dark:border-[#FF7869]/40", isTranscribing ? "opacity-100 translate-x-0" : "opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0")
  }, isTranscribing ? "Recording" : "Speak"), /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: isTranscribing ? faStop : faMicrophone,
    size: "lg"
  })))), /*#__PURE__*/React.createElement("div", {
    className: classNames("relative transition-all duration-300 ml-3", isTranscribing ? "opacity-0 translate-x-2 pointer-events-none" : "opacity-100 translate-x-0")
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    onMouseEnter: function onMouseEnter() {
      return setShowComingSoonPopup("voice");
    },
    onMouseLeave: function onMouseLeave() {
      return setShowComingSoonPopup(null);
    },
    className: "p-2 rounded-full text-gray-200 hover:text-teal-200 dark:text-gray-400 dark:hover:text-teal-600"
  }, /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faWaveformLines,
    size: "lg"
  })), /*#__PURE__*/React.createElement(ComingSoonPopup, {
    feature: "voice",
    isVisible: showComingSoonPopup === "voice",
    onClose: function onClose() {
      return setShowComingSoonPopup(null);
    }
  })))), showPromptDropdown && filteredPrompts.length > 0 && /*#__PURE__*/React.createElement("div", {
    className: "absolute left-0 bottom-[70%] mb-0 z-50 min-w-[250px] max-w-[350px] w-10/12 scrollbar-light dark:scrollbar z-100",
    style: {
      pointerEvents: "auto"
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-lg shadow-2xl max-h-64 overflow-y-auto"
  }, function () {
    var sectionMap = {};
    filteredPrompts.forEach(function (prompt) {
      if (!sectionMap[prompt.section]) {
        sectionMap[prompt.section] = {
          icon: prompt.icon,
          prompts: []
        };
      }
      sectionMap[prompt.section].prompts.push(prompt);
    });
    var orderedSections = promptsData.sections.map(function (section) {
      return section.title;
    }).filter(function (title) {
      return sectionMap[title];
    });
    return orderedSections.map(function (sectionTitle, sectionIdx) {
      return /*#__PURE__*/React.createElement(React.Fragment, {
        key: sectionTitle
      }, /*#__PURE__*/React.createElement("div", {
        className: "sticky top-0 z-10 flex items-center gap-2 px-4 pt-3 pb-1 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 font-semibold text-gray-700 dark:text-gray-200 text-sm"
      }, sectionMap[sectionTitle].icon && /*#__PURE__*/React.createElement(AwesomeIcon, {
        name: sectionIconMap[sectionMap[sectionTitle].icon],
        className: "w-4 h-4 text-brand-peach"
      }), /*#__PURE__*/React.createElement("span", null, sectionTitle)), sectionMap[sectionTitle].prompts.map(function (prompt, idx) {
        var globalIdx = filteredPrompts.findIndex(function (p) {
          return p.name === prompt.name && p.section === prompt.section;
        });
        return /*#__PURE__*/React.createElement("div", {
          key: prompt.name + idx,
          ref: function ref(el) {
            return promptItemRefs.current[globalIdx] = el;
          },
          className: "flex items-center px-4 py-2 cursor-pointer transition-colors duration-150 border-l-4 border-transparent\n                              ".concat(globalIdx === selectedPromptIndex ? "bg-teal-100 dark:bg-teal-900 border-teal-400/80" : "hover:bg-teal-50 dark:hover:bg-teal-900", "\n                              "),
          onMouseDown: function onMouseDown(e) {
            e.preventDefault();
            selectPrompt(prompt);
          },
          onMouseEnter: function onMouseEnter() {
            return setSelectedPromptIndex(globalIdx);
          }
        }, /*#__PURE__*/React.createElement("span", {
          className: "font-semibold text-gray-800 dark:text-gray-100"
        }, prompt.name));
      }));
    });
  }(), filteredPrompts.length === 0 && /*#__PURE__*/React.createElement("div", {
    className: "px-4 py-2 text-gray-400"
  }, "No prompts found"))), /*#__PURE__*/React.createElement("div", {
    className: "flex justify-center my-3"
  }, /*#__PURE__*/React.createElement("div", {
    className: "sm:text-md text-sm text-gray-500 dark:text-gray-400"
  }, "AI-powered assistance \u2022 Secure and private"))));
};
export default ChatInput;